import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* ════════════════════ USE MOBILE HOOK ════════════════════ */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

/* ════════════════════ CONFETTI ════════════════════ */
function Confetti({ active }) {
  const isMobile = useIsMobile();
  const particles = useMemo(() =>
    Array.from({ length: isMobile ? 200 : 600 }, (_, i) => {
      const colors = ["#00A95C","#FFD700","#D01C1F","#fff","#1fa855","#e8b100","#c0392b","#27ae60","#f5d442","#ff6b6b","#00c964","#ffd900","#ff4444","#ffffff","#88cc44"];
      return {
        id: i, x: Math.random() * 100, color: colors[i % colors.length],
        delay: Math.random() * 3.0, dur: 2.5 + Math.random() * 4.0,
        wobble: (Math.random() - 0.5) * 70, rot: Math.random() * 1800 - 900,
        w: 4 + Math.random() * 12, h: 3 + Math.random() * 18,
        r: Math.random() > 0.4 ? "2px" : "50%",
        startY: -(2 + Math.random() * 20),
      };
    }), [isMobile]);
  if (!active) return null;
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 100, overflow: "hidden" }}>
      {particles.map(p => (
        <motion.div key={p.id}
          initial={{ x: `${p.x}vw`, y: `${p.startY}vh`, rotate: 0, opacity: 1 }}
          animate={{ y: "115vh", rotate: p.rot, x: `${p.x + p.wobble}vw`, opacity: [1, 1, 1, 0.7, 0] }}
          transition={{ duration: p.dur, delay: p.delay, ease: [0.12, 0.6, 0.35, 1] }}
          style={{ position: "absolute", width: p.w, height: p.h, borderRadius: p.r, background: p.color }}
        />
      ))}
    </div>
  );
}

/* ════════════════════ PLEXUS NETWORK ════════════════════ */
function Plexus({ color = "rgba(255,255,255,", nodeCount = 50, maxDist = 120 }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const nodesRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const W = () => canvas.width / (window.devicePixelRatio || 1);
    const H = () => canvas.height / (window.devicePixelRatio || 1);

    if (!nodesRef.current || nodesRef.current.length !== nodeCount) {
      nodesRef.current = Array.from({ length: nodeCount }, () => ({
        x: Math.random() * 1400,
        y: Math.random() * 800,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.25,
        r: 1 + Math.random() * 1.5,
      }));
    }
    const nodes = nodesRef.current;

    const draw = () => {
      const w = W(), h = H();
      ctx.clearRect(0, 0, w, h);

      for (let n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < -20) n.x = w + 20;
        if (n.x > w + 20) n.x = -20;
        if (n.y < -10) n.y = h + 10;
        if (n.y > h + 10) n.y = -10;
      }

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < maxDist) {
            const alpha = (1 - d / maxDist) * 0.35;
            ctx.strokeStyle = color + alpha + ")";
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      for (let n of nodes) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = color + "0.6)";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r + 2, 0, Math.PI * 2);
        ctx.fillStyle = color + "0.08)";
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [color, nodeCount, maxDist]);

  return (
    <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />
  );
}

/* ════════════════════ LOADING SCREEN ════════════════════ */
function LoadingScreen({ active, doorsOpen, onComplete }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!doorsOpen) return;
    let p = 0;
    const interval = setInterval(() => {
      p += 0.8 + Math.random() * 2.5;
      if (p >= 100) {
        p = 100;
        clearInterval(interval);
        setTimeout(onComplete, 800);
      }
      setProgress(Math.min(100, Math.round(p)));
    }, 50);
    return () => clearInterval(interval);
  }, [doorsOpen, onComplete]);

  if (!active) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 195,
      background: "#f8f9f7",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      fontFamily: "'Louguiya', sans-serif",
    }}>
      <Plexus color="rgba(0,169,92," nodeCount={60} maxDist={140} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        style={{ textAlign: "center", position: "relative", zIndex: 1, padding: "0 24px" }}
      >
        <p style={{ fontSize: 22, fontWeight: 700, color: "#1a2e1f", margin: 0, direction: "rtl", lineHeight: 1.8 }}>
          جاري تحميل المنصة...
        </p>
        <p style={{ fontSize: 14, fontWeight: 400, color: "#8a9a8e", margin: "8px 0 0", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.15em" }}>
          Chargement de la plateforme
        </p>
        <div style={{ width: "min(280px, 80vw)", margin: "36px auto 0" }}>
          <div style={{ width: "100%", height: 3, borderRadius: 2, background: "rgba(0,169,92,0.1)", overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 2, background: "linear-gradient(90deg, #00A95C, #00c964)", width: `${progress}%`, transition: "width 0.08s linear" }} />
          </div>
          <p style={{ textAlign: "center", marginTop: 12, fontSize: 12, fontWeight: 400, color: "#a0aaa4", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.12em" }}>
            {progress}%
          </p>
        </div>
      </motion.div>
      <div style={{ position: "absolute", bottom: 28, left: 0, right: 0, textAlign: "center", fontSize: 11, color: "#b0bab4", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.1em" }}>
        © {new Date().getFullYear()} — الوكالة الرقمية للدولة · AN-ETAT
      </div>
    </div>
  );
}

/* ════════════════════ DOOR SPLIT ════════════════════ */
function DoorSplit({ active, onDone }) {
  const isMobile = useIsMobile();
  if (!active) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, pointerEvents: "none" }}>

      {/* TOP CENTER — Republic header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        style={{
          position: "absolute", top: 0, left: 0, right: 0, zIndex: 220,
          display: "flex", flexDirection: "column", alignItems: "center",
          padding: isMobile ? "16px 0 14px" : "28px 0 20px",
          background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.9) 70%, transparent 100%)",
        }}
      >
        <img src="/images/logo_rim.png" alt="" style={{ height: isMobile ? 44 : 64, width: "auto", objectFit: "contain", marginBottom: 6 }} />
        <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif" }}>الجمهورية الإسلامية الموريتانية</div>
        <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 700, color: "#8a9a8e", fontFamily: "'Louguiya', sans-serif", marginTop: 3 }}>شرف - إخاء - عدل</div>
      </motion.div>

      {/* Left door */}
      <motion.div
        initial={{ x: "0%" }} animate={{ x: "-100%" }}
        transition={{ duration: 2.4, delay: 3.5, ease: [0.76, 0, 0.24, 1] }}
        onAnimationComplete={onDone}
        style={{
          position: "absolute", top: 0, left: 0, width: "50%", height: "100%",
          background: "#fff",
          boxShadow: "4px 0 40px rgba(0,0,0,0.12)",
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: isMobile ? "100px 12px 40px" : "130px 40px 60px",
        }}
      >
        <div style={{ textAlign: "center", direction: "rtl", marginBottom: isMobile ? 14 : 24 }}>
          <div style={{ fontSize: isMobile ? 16 : 30, fontWeight: 700, color: "#1a2e1f", fontFamily: "'Louguiya', sans-serif", lineHeight: 1.6 }}>وزارة التجهيز والنقل</div>
          <div style={{ fontSize: isMobile ? 8 : 14, fontWeight: 400, color: "#555", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.06em", marginTop: 4 }}>Ministère de l'Équipement et des Transports</div>
        </div>
        <div style={{ width: "60%", height: 1, background: "rgba(26,110,138,0.15)", marginBottom: isMobile ? 14 : 28 }} />
        <img src="/images/arotr.png" alt="" style={{ height: isMobile ? 60 : 110, width: "auto", objectFit: "contain", marginBottom: isMobile ? 10 : 18 }} />
        <div style={{ fontSize: isMobile ? 14 : 24, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif", direction: "rtl", textAlign: "center", lineHeight: 1.6 }}>سلطة تنظيم النقل الطرقي</div>
        <div style={{ fontSize: isMobile ? 7 : 12, fontWeight: 400, color: "#555", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.08em", marginTop: 4, textAlign: "center" }}>Autorité de Régulation du Transport Routier</div>
      </motion.div>

      {/* Center handles */}
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.5, delay: 3.3 }}
        style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)", zIndex: 215,
          display: "flex", alignItems: "center", gap: 4,
        }}
      >
        <div style={{ width: isMobile ? 10 : 14, height: isMobile ? 60 : 90, borderRadius: "7px 0 0 7px", background: "linear-gradient(180deg, #f5e070, #e0c840, #c9a82c, #b89428, #c9a82c, #e0c840, #f5e070)", boxShadow: "-2px 3px 12px rgba(0,0,0,0.2)", border: "1px solid rgba(168,138,32,0.3)", borderRight: "none" }} />
        <div style={{ width: isMobile ? 4 : 6, height: isMobile ? 70 : 110, borderRadius: 3, background: "linear-gradient(180deg, #00A95C, #FFD700, #D01C1F)", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }} />
        <div style={{ width: isMobile ? 10 : 14, height: isMobile ? 60 : 90, borderRadius: "0 7px 7px 0", background: "linear-gradient(180deg, #f5e070, #e0c840, #c9a82c, #b89428, #c9a82c, #e0c840, #f5e070)", boxShadow: "2px 3px 12px rgba(0,0,0,0.2)", border: "1px solid rgba(168,138,32,0.3)", borderLeft: "none" }} />
      </motion.div>

      {/* Right door */}
      <motion.div
        initial={{ x: "0%" }} animate={{ x: "100%" }}
        transition={{ duration: 2.4, delay: 3.5, ease: [0.76, 0, 0.24, 1] }}
        style={{
          position: "absolute", top: 0, right: 0, width: "50%", height: "100%",
          background: "#fff",
          boxShadow: "-4px 0 40px rgba(0,0,0,0.12)",
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: isMobile ? "100px 12px 40px" : "130px 40px 60px",
        }}
      >
        <div style={{ textAlign: "center", direction: "rtl", marginBottom: isMobile ? 14 : 24 }}>
          <div style={{ fontSize: isMobile ? 14 : 30, fontWeight: 700, color: "#1a2e1f", fontFamily: "'Louguiya', sans-serif", lineHeight: 1.6 }}>وزارة التحول الرقمي وعصرنة الإدارة</div>
          <div style={{ fontSize: isMobile ? 7 : 14, fontWeight: 400, color: "#555", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.06em", marginTop: 4, textAlign: "center" }}>Ministère de la Transformation Numérique et de la Modernisation de l'Administration</div>
        </div>
        <div style={{ width: "60%", height: 1, background: "rgba(0,169,92,0.15)", marginBottom: isMobile ? 14 : 28 }} />
        <img src="/images/anetat.png" alt="" style={{ height: isMobile ? 60 : 110, width: "auto", objectFit: "contain", marginBottom: isMobile ? 10 : 18 }} />
        <div style={{ fontSize: isMobile ? 14 : 24, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif", direction: "rtl", textAlign: "center", lineHeight: 1.6 }}>الوكالة الرقمية للدولة</div>
        <div style={{ fontSize: isMobile ? 7 : 12, fontWeight: 400, color: "#555", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.08em", marginTop: 4, textAlign: "center" }}>Agence Numérique de l'État — AN-ETAT</div>
      </motion.div>
    </div>
  );
}

/* ══════════════════════ MOBILE HEADER ══════════════════════ */
function MobileHeader() {
  return (
    <motion.header
      initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      style={{ background: "#fff", padding: "16px 16px 12px", direction: "rtl", position: "relative", zIndex: 10 }}
    >
      {/* République */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 16 }}>
        <img src="/images/logo_rim.png" alt="" style={{ height: 44, width: "auto", objectFit: "contain" }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif", marginTop: 6 }}>الجمهورية الإسلامية الموريتانية</div>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7c6e", fontFamily: "'Louguiya', sans-serif", marginTop: 2 }}>شرف - إخاء - عدل</div>
      </div>

      {/* Two ministries side by side */}
      <div style={{ display: "flex", gap: 12, alignItems: "stretch" }}>
        {/* Right — Digital Ministry + AN-ETAT */}
        <div style={{ flex: 1, borderRight: "2px solid #00A95C", paddingRight: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif", lineHeight: 1.6 }}>وزارة التحول الرقمي وعصرنة الإدارة</div>
          <div style={{ fontSize: 7, fontWeight: 400, color: "#8a9a8e", fontFamily: "'LouguiyaFR', sans-serif", marginTop: 2, letterSpacing: "0.04em" }}>Ministère de la Transformation Numérique</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <img src="/images/anetat.png" alt="" style={{ height: 32, width: "auto", objectFit: "contain" }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif", lineHeight: 1.4 }}>الوكالة الرقمية للدولة</div>
              <div style={{ fontSize: 7, fontWeight: 400, color: "#8a9a8e", fontFamily: "'LouguiyaFR', sans-serif" }}>AN-ETAT</div>
            </div>
          </div>
        </div>

        {/* Left — Transport Ministry + AROTR */}
        <div style={{ flex: 1, borderLeft: "2px solid #00A95C", paddingLeft: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif", lineHeight: 1.6, textAlign: "left" }}>وزارة التجهيز والنقل</div>
          <div style={{ fontSize: 7, fontWeight: 400, color: "#8a9a8e", fontFamily: "'LouguiyaFR', sans-serif", marginTop: 2, letterSpacing: "0.04em", textAlign: "left" }}>Ministère de l'Équipement et des Transports</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexDirection: "row-reverse" }}>
            <img src="/images/arotr.png" alt="" style={{ height: 32, width: "auto", objectFit: "contain" }} />
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif", lineHeight: 1.4 }}>سلطة تنظيم النقل الطرقي</div>
              <div style={{ fontSize: 7, fontWeight: 400, color: "#8a9a8e", fontFamily: "'LouguiyaFR', sans-serif" }}>AROTR</div>
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  );
}

/* ══════════════════════ DESKTOP HEADER ══════════════════════ */
function DesktopHeader() {
  return (
    <motion.header
      initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "stretch",
        padding: "1vh 40px", direction: "rtl", position: "relative", zIndex: 10,
        background: "#fff", flexShrink: 0,
      }}
    >
      {/* Right block — Digital Ministry + AN-ETAT */}
      <div style={{ flex: 1, borderRight: "3px solid #00A95C", paddingRight: 16, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif" }}>وزارة التحول الرقمي وعصرنة الإدارة</div>
          <div style={{ fontSize: 10, fontWeight: 400, color: "#8a9a8e", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.06em", marginTop: 2 }}>Ministère de la Transformation Numérique et de la Modernisation de l'Administration</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
          <div style={{ flex: 1, height: 1, background: "rgba(0,169,92,0.18)" }} />
          <div style={{ width: 6, height: 6, background: "#00A95C", transform: "rotate(45deg)", flexShrink: 0, marginLeft: 4 }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src="/images/anetat.png" alt="" style={{ height: 38, width: "auto", objectFit: "contain" }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif", lineHeight: 1.5 }}>الوكالة الرقمية للدولة</div>
            <div style={{ fontSize: 10, fontWeight: 400, color: "#8a9a8e", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.06em" }}>Agence Numérique de l'État — AN-ETAT</div>
          </div>
        </div>
      </div>

      {/* Center — République */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 2px", flexShrink: 0 }}>
        <img src="/images/logo_rim.png" alt="" style={{ height: 40, width: "auto", objectFit: "contain" }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif", marginTop: 4, letterSpacing: "0.02em" }}>الجمهورية الإسلامية الموريتانية</div>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7c6e", fontFamily: "'Louguiya', sans-serif", marginTop: 1 }}>شرف - إخاء - عدل</div>
      </div>

      {/* Left block — Transport Ministry + AROTR */}
      <div style={{ flex: 1, borderLeft: "3px solid #00A95C", paddingLeft: 16, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ marginBottom: 6, textAlign: "left" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif" }}>وزارة التجهيز والنقل</div>
          <div style={{ fontSize: 10, fontWeight: 400, color: "#8a9a8e", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.06em", marginTop: 2 }}>Ministère de l'Équipement et des Transports</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
          <div style={{ width: 6, height: 6, background: "#00A95C", transform: "rotate(45deg)", flexShrink: 0, marginRight: 4 }} />
          <div style={{ flex: 1, height: 1, background: "rgba(0,169,92,0.18)" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "flex-end" }}>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Louguiya', sans-serif", lineHeight: 1.5 }}>سلطة تنظيم النقل الطرقي</div>
            <div style={{ fontSize: 10, fontWeight: 400, color: "#8a9a8e", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.06em" }}>Autorité de Régulation du Transport Routier — AROTR</div>
          </div>
          <img src="/images/arotr.png" alt="" style={{ height: 38, width: "auto", objectFit: "contain" }} />
        </div>
      </div>
    </motion.header>
  );
}

/* ══════════════════════ MAIN APP ══════════════════════ */
export default function App() {
  const [phase, setPhase] = useState("idle");
  const isMobile = useIsMobile();
  const redirectUrl = "https://arotr.services.anetat.gov.mr/login";

  const [countdown, setCountdown] = useState(null);
  const showConfetti = ["confetti", "doors", "done"].includes(phase);
  const showDoors = ["doors", "done"].includes(phase);
  const isPreCut = phase === "idle";
  const showCountdown = phase === "countdown";

  const handleStart = useCallback(() => {
    if (phase !== "idle") return;
    setPhase("countdown");
    setCountdown(5);
  }, [phase]);

  useEffect(() => {
    if (phase !== "countdown" || countdown === null) return;
    if (countdown <= 0) {
      setPhase("cutting");
      setTimeout(() => setPhase("cut"), 800);
      setTimeout(() => setPhase("confetti"), 1200);
      setTimeout(() => setPhase("doors"), 5500);
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  const today = new Date();
  const dateStr = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, "0")}/${String(today.getDate()).padStart(2, "0")}`;

  return (
    <div style={{
      height: "100vh", display: "flex", flexDirection: "column",
      fontFamily: "'Louguiya', 'Noto Kufi Arabic', sans-serif",
      background: "#f8f9f7",
      position: "relative", overflow: "hidden",
    }}>
      {/* Subtle radial glow */}
      <div style={{
        position: "absolute", top: "35%", left: "50%", transform: "translate(-50%, -50%)",
        width: isMobile ? 400 : 900, height: isMobile ? 400 : 900, borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle, rgba(0,169,92,0.03) 0%, transparent 60%)",
      }} />

      {/* Tricolor stripe */}
      <div style={{ height: isMobile ? 4 : "0.5vh", flexShrink: 0, background: "linear-gradient(90deg, #00A95C 0%, #00A95C 33%, #FFD700 33%, #FFD700 66%, #D01C1F 66%, #D01C1F 100%)" }} />

      {/* HEADER */}
      {isMobile ? <MobileHeader /> : <DesktopHeader />}

      {/* Bottom accent line */}
      <div style={{ height: 2, flexShrink: 0, background: "linear-gradient(90deg, #00A95C 0%, #00A95C 33%, #FFD700 33%, #FFD700 66%, #D01C1F 66%, #D01C1F 100%)" }} />

      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        style={{ textAlign: "center", padding: isMobile ? "16px 16px 0" : "1.2vh 32px 0", maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 10, flexShrink: 1 }}
      >
        {/* Arabic */}
        <p style={{
          fontSize: isMobile ? 16 : "clamp(14px, 2.2vh, 22px)", fontWeight: 700, color: "#1a2e1f", margin: 0, lineHeight: 1.6,
          direction: "rtl", fontFamily: "'Louguiya', sans-serif",
        }}>
          إطلاق المنصة الرقمية لسلطة تنظيم النقل الطرقي، في إطار استراتيجية إدارة التنظيم الرامية إلى رقمنة الخدمات وتسهيل الإجراءات
        </p>

        {/* Losange divider */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }} animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 8 : 12, margin: isMobile ? "8px auto" : "0.8vh auto", transformOrigin: "center" }}
        >
          <div style={{ flex: 1, maxWidth: isMobile ? 50 : 100, height: 1, background: "linear-gradient(90deg, transparent, #00A95C)" }} />
          <div style={{ width: isMobile ? 5 : 7, height: isMobile ? 5 : 7, background: "#00A95C", transform: "rotate(45deg)", flexShrink: 0 }} />
          <div style={{ width: isMobile ? 4 : 5, height: isMobile ? 4 : 5, background: "#FFD700", transform: "rotate(45deg)", flexShrink: 0 }} />
          <div style={{ width: isMobile ? 7 : 10, height: isMobile ? 7 : 10, background: "#00A95C", transform: "rotate(45deg)", flexShrink: 0 }} />
          <div style={{ width: isMobile ? 4 : 5, height: isMobile ? 4 : 5, background: "#FFD700", transform: "rotate(45deg)", flexShrink: 0 }} />
          <div style={{ width: isMobile ? 5 : 7, height: isMobile ? 5 : 7, background: "#00A95C", transform: "rotate(45deg)", flexShrink: 0 }} />
          <div style={{ flex: 1, maxWidth: isMobile ? 50 : 100, height: 1, background: "linear-gradient(270deg, transparent, #00A95C)" }} />
        </motion.div>

        {/* French */}
        <p style={{
          fontSize: isMobile ? 12 : "clamp(11px, 1.5vh, 14px)", fontWeight: 400, color: "#6b7c6e", margin: 0, lineHeight: 1.5,
          direction: "ltr", fontFamily: "'LouguiyaFR', 'Segoe UI', sans-serif",
        }}>
          Lancement de la plateforme numérique de l'Autorité de Régulation du Transport Routier, s'inscrivant dans la stratégie de la Direction de la Réglementation pour la digitalisation des services et la simplification des procédures
        </p>

        {/* Date */}
        <p style={{ fontSize: isMobile ? 13 : "clamp(11px, 1.4vh, 13px)", fontWeight: 400, color: "#8a9a8e", margin: isMobile ? "4px 0 0" : "0.5vh 0 0", letterSpacing: "0.04em", fontFamily: "'LouguiyaFR', sans-serif" }}>
          بتاريخ: {dateStr}
        </p>
      </motion.div>

      {/* Main content */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column", justifyContent: "center",
        alignItems: "center", position: "relative", zIndex: 10, padding: isMobile ? "0 12px 40px" : "0 20px 0",
        minHeight: 0,
      }}>

        {/* CURTAIN BANNER */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={{ position: "relative", width: "100vw", marginLeft: "calc(-50vw + 50%)" }}
        >
          <AnimatePresence>
            {!showConfetti && (
              <motion.div
                exit={{ scaleY: 0, opacity: 0 }}
                transition={{ duration: 1.0, ease: [0.76, 0, 0.24, 1] }}
                style={{
                  background: "linear-gradient(135deg, #00A95C 0%, #00874a 40%, #005a2e 100%)",
                  padding: isMobile ? "20px 16px" : "1.5vh 60px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: isMobile ? 12 : 24,
                  direction: "rtl",
                  boxShadow: "0 8px 40px rgba(0,100,50,0.2)",
                  position: "relative",
                  transformOrigin: "top center",
                  overflow: "hidden",
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                <Plexus />

                {/* Logo */}
                <div style={{
                  width: isMobile ? 56 : "clamp(48px, 7vh, 60px)", height: isMobile ? 56 : "clamp(48px, 7vh, 60px)", borderRadius: "50%", background: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, position: "relative", zIndex: 1,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                }}>
                  <img src="/images/arotr.png" alt="" style={{ height: isMobile ? 38 : 50, width: isMobile ? 38 : 50, objectFit: "contain" }} />
                </div>

                {/* Separator — hidden on mobile */}
                {!isMobile && <div style={{ width: 1, height: 36, background: "rgba(255,255,255,0.25)", flexShrink: 0, position: "relative", zIndex: 1 }} />}

                {/* Text */}
                <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
                  <p style={{
                    fontSize: isMobile ? 16 : "clamp(14px, 2vh, 18px)", fontWeight: 700, color: "#fff", margin: 0,
                    fontFamily: "'Louguiya', sans-serif", lineHeight: 1.7,
                  }}>
                    المنصة الرقمية لسلطة تنظيم النقل الطرقي
                  </p>
                  <p style={{
                    fontSize: isMobile ? 10 : 12, fontWeight: 400, color: "rgba(255,255,255,0.6)", margin: "4px 0 0",
                    fontFamily: "'LouguiyaFR', sans-serif", direction: "ltr", textAlign: "center",
                    letterSpacing: "0.12em", textTransform: "uppercase",
                  }}>
                    Plateforme Numérique — AROTR
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* COUNTDOWN */}
        <AnimatePresence mode="wait">
          {showCountdown && countdown !== null && countdown > 0 && (
            <motion.div
              key={countdown}
              initial={{ scale: 0.3, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 1.8, opacity: 0, y: -10 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{ marginTop: isMobile ? 32 : "4vh", textAlign: "center", position: "relative" }}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                style={{
                  width: isMobile ? 120 : 160, height: isMobile ? 120 : 160, borderRadius: "50%",
                  border: "2px solid rgba(0,169,92,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto", position: "relative",
                  overflow: "hidden", background: "rgba(0,169,92,0.03)",
                }}
              >
                <Plexus color="rgba(0,169,92," nodeCount={15} maxDist={60} />
                <svg width={isMobile ? "120" : "160"} height={isMobile ? "120" : "160"} style={{ position: "absolute", top: 0, left: 0 }}>
                  <motion.circle
                    cx={isMobile ? "60" : "80"} cy={isMobile ? "60" : "80"} r={isMobile ? "56" : "76"}
                    fill="none" stroke="#00A95C" strokeWidth="2"
                    strokeDasharray={isMobile ? 352 : 478}
                    strokeLinecap="round"
                    initial={{ strokeDashoffset: isMobile ? 352 : 478 }}
                    animate={{ strokeDashoffset: 0 }}
                    transition={{ duration: 1, ease: "linear" }}
                    style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
                  />
                </svg>
                <span style={{
                  fontSize: isMobile ? 52 : 72, fontWeight: 400, color: "#00A95C",
                  fontFamily: "'Digital7', 'Courier New', monospace",
                  lineHeight: 1, position: "relative", zIndex: 1,
                }}>
                  {countdown}
                </span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Success */}
        <AnimatePresence>
          {showConfetti && !showDoors && (
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }} transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              style={{ marginTop: isMobile ? 32 : "4vh", textAlign: "center", direction: "rtl", padding: "0 16px" }}
            >
              <p style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, color: "#00A95C", margin: 0, fontFamily: "'Louguiya', sans-serif" }}>
                🎉 تم الإطلاق بنجاح
              </p>
              <p style={{ fontSize: isMobile ? 13 : 16, fontWeight: 400, color: "#6b7c6e", margin: "12px 0 0", fontFamily: "'LouguiyaFR', sans-serif" }}>
                Lancement réussi !
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* BUTTON */}
      <AnimatePresence>
        {isPreCut && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: 14 }}
            transition={{ duration: 0.4 }}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: isMobile ? "0 16px 8px" : "0 20px 0.5vh", position: "relative", zIndex: 10 }}
          >
            <motion.button
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              whileHover={{ scale: 1.04, boxShadow: "0 14px 50px rgba(0,169,92,0.35)" }}
              whileTap={{ scale: 0.96 }}
              onClick={handleStart}
              style={{
                padding: isMobile ? "16px 60px" : "1.5vh 80px", border: "none",
                borderRadius: 16, cursor: "pointer",
                background: "linear-gradient(135deg, #00A95C 0%, #00874a 50%, #005a2e 100%)",
                boxShadow: "0 8px 36px rgba(0,169,92,0.28)",
                position: "relative", overflow: "hidden",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                width: isMobile ? "100%" : "auto",
                maxWidth: isMobile ? 320 : "none",
              }}
            >
              <motion.div
                animate={{ x: ["-130%", "230%"] }}
                transition={{ duration: 3, repeat: Infinity, repeatDelay: 2.5, ease: "easeInOut" }}
                style={{
                  position: "absolute", top: 0, left: 0, width: "30%", height: "100%",
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                }}
              />
              <span style={{ fontSize: isMobile ? 22 : "clamp(18px, 2.5vh, 24px)", fontWeight: 700, color: "#fff", fontFamily: "'Louguiya', sans-serif", letterSpacing: "0.06em", position: "relative", zIndex: 1 }}>
                إنطلاق
              </span>
              <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 400, color: "rgba(255,255,255,0.75)", fontFamily: "'LouguiyaFR', sans-serif", letterSpacing: "0.2em", textTransform: "uppercase", position: "relative", zIndex: 1 }}>
                Lancer
              </span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }}
        style={{
          textAlign: "center", padding: isMobile ? "14px 0 20px" : "0.8vh 0 1vh",
          fontSize: isMobile ? 11 : 12, color: "#a0aaa4", letterSpacing: "0.1em", fontWeight: 400,
          position: "relative", zIndex: 10, fontFamily: "'LouguiyaFR', sans-serif",
          flexShrink: 0,
        }}
      >
        © {new Date().getFullYear()} — الوكالة الرقمية للدولة · AN-ETAT
      </motion.footer>

      <Confetti active={showConfetti} />
      <LoadingScreen
        active={showDoors}
        doorsOpen={phase === "done" || phase === "loading"}
        onComplete={() => { window.location.href = redirectUrl; }}
      />
      <DoorSplit active={showDoors} onDone={() => { setPhase("done"); }} />
    </div>
  );
}
